/**
 * /login — public. The sign-in card: the logo, Welcome back, one line, one button (Microsoft Entra ID).
 * Nothing else is said here (1.5.10 — Elijah, 5 Oct 2026).
 */
import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInUrl, useMe } from '@/auth/useMe';
import { clearTodayCopy } from '@/lib/today-cache';

export function LoginPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo') ?? '/';
  // 1 Nov 2026 (D-1102-CREW-1): nobody is signed in here — no stored copy of Today is kept for the next person
  useEffect(() => {
    if (!me.isLoading && !me.data?.principal) clearTodayCopy();
  }, [me.isLoading, me.data]);
  // 14 Oct 2026 (react-router advisory GHSA-wrjc-x8rr-h8h6): a backslash or control character can turn "/\\host" into
  // another site — only a plain path on this site is followed back after sign-in
  const plainPath = returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\') && ![...returnTo].some((c) => c.charCodeAt(0) < 32 || c === ' ');
  const safeReturn = plainPath ? returnTo : '/';
  if (me.data?.principal) return <Navigate to={safeReturn} replace />;
  return (
    <div id="signin">
      <main id="main" className="card-signin" aria-labelledby="h-signin">
        <img className="logo logo-light" src="/brand/logo-light.png" alt="Associated Blind Foundation" />
        <img className="logo logo-dark" src="/brand/logo-dark.png" alt="Associated Blind Foundation" />
        <div>
          <h1 id="h-signin">{t('signin.welcomeBack')}</h1>
          <p style={{ margin: '8px auto 0' }}>{t('signin.sub')}</p>
        </div>
        <a className="btn-ms" href={signInUrl(safeReturn)} data-testid="signin">
          <svg viewBox="0 0 21 21" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          <span>{t('signin.button')}</span>
        </a>
      </main>
    </div>
  );
}
